import type { ColumnDef } from '@tanstack/react-table';

import type { StoredColumnSettings } from '../types';
import { columnDefToStoredSettings } from '../types';

/**
 * This function determines what settings are saved in `localStorage`.
 *
 * We only save the user's settings when they are different from the default.
 * This allows us to change the column configurations in the future without
 * the user's saved settings overriding us.
 *
 * Example 1:
 * - `propsColumns` has an Avatar column with the property `sticky: true`
 * - <ColumnTemplate> sets that column to be `sticky: false`
 * - ✅ The setting is saved.
 *
 * Example 2:
 * - `propsColumns` has a Reason column with the property `visible: false`
 * - <ColumnTemplate> sets that column to be `visible: false`
 * - ❌ The setting is not saved.
 *
 * Example 3:
 * - `propsColumns` has a Type column that does not have a `sticky` property
 * - <ColumnTemplate> sets that column to be `sticky: false`
 * - ❌ The setting is not saved.
 *
 * Example 4:
 * - `propsColumns` has a Type column that does not have a `visible` property
 * - <ColumnTemplate> sets that column to be `visible: false`
 * - ✅ The setting is saved.
 */
export function omitColumnDefaults<T>(
	defaultColumns: ColumnDef<T>[],
	columns: StoredColumnSettings[]
): StoredColumnSettings[] {
	return columns.map((column) => {
		const defaultColumn = defaultColumns.find(
			(defaultColumn_) => defaultColumn_.id === column.id
		);

		const storedSettings: StoredColumnSettings = { id: column.id };
		if (!defaultColumn) return storedSettings;

		const defaultSettings = columnDefToStoredSettings(defaultColumn);
		for (const prop of Object.keys(column) as (keyof StoredColumnSettings)[]) {
			if (
				column[prop] !== undefined &&
				column[prop] !== defaultSettings[prop]
			) {
				// @ts-expect-error We know this is a valid property
				storedSettings[prop] = column[prop];
			}
		}

		return storedSettings;
	});
}

export function findColumnDiffs<T>(
	oldColumns: ColumnDef<T>[],
	newColumns: ColumnDef<T>[]
) {
	const differences: string[] = [];

	for (const newColumn of newColumns) {
		const oldColumn = oldColumns.find((c) => c.id === newColumn.id);
		if (!oldColumn) continue;

		if (oldColumn.meta?.sticky !== newColumn.meta?.sticky) {
			differences.push(`${newColumn.meta?.name}:sticky`);
		} else if (oldColumn.meta?.visible !== newColumn.meta?.visible) {
			differences.push(newColumn.meta?.name ?? '');
		}
	}

	if (differences.length === 0) return '';

	return differences.join(', ');
}
